import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { persistentStorage } from '../../../lib/storage';
import { apiV1Request } from '../../requests';
import {
    type OpenApiGetCloudPayCallbackUrlPathParameters,
    type OpenApiGetCloudPayCallbackUrlResponseSuccess,
    OpenApiGetOnboardingStagesMethod,
    type OpenApiGetOnboardingStagesResponseSuccess,
    OpenApiGetOnboardingStagesUrl,
    type OpenApiUpdateStageCompletionRequest,
    type OpenApiUpdateStageCompletionResponseSuccess,
    type OpenApiUpdateSubtaskCompletionRequest,
    type OpenApiUpdateSubtaskCompletionResponseSuccess,
} from '../../schema';
import { ONBOARDING_FINISHED_STORAGE_KEY } from './constants';
import { getOnboardingConfig } from './onboardingVariant';
import type { OnboardingStage, OnboardingSubtask } from './types';

const OnboardingTag = 'Onboarding' as const;

interface ChangeOnboardingStageRequest extends OpenApiUpdateStageCompletionRequest {
    slug: OnboardingStage;
}

interface ChangeOnboardingSubtaskRequest extends OpenApiUpdateSubtaskCompletionRequest {
    slug: OnboardingSubtask;
}

export const api = createApi({
    name: 'shared/api/onboarding/api/api',
    tagTypes: [OnboardingTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getOnboardingStages: builder.query<OpenApiGetOnboardingStagesResponseSuccess, void>({
                queryFn: async (_args, api, extraOptions) => {
                    if (persistentStorage.getItem(ONBOARDING_FINISHED_STORAGE_KEY) === '1') {
                        const { publishStageSlug } = getOnboardingConfig();

                        // Возвращаем моковые данные с пройденными этапами
                        return {
                            data: {
                                stages: [
                                    {
                                        slug: publishStageSlug,
                                        is_completed: true,
                                        subtasks: [],
                                        name: '',
                                    },
                                ],
                            },
                        };
                    }

                    const query = createBaseQuery(apiV1Request);

                    const result = await query(
                        {
                            url: OpenApiGetOnboardingStagesUrl,
                            method: OpenApiGetOnboardingStagesMethod,
                        },
                        api,
                        extraOptions
                    );

                    return { data: result.data as OpenApiGetOnboardingStagesResponseSuccess };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const result = queryFulfilledResult;
                    const { publishStageSlug } = getOnboardingConfig();
                    const publishStage = result.data.stages.find((stage) => stage.slug === publishStageSlug);

                    if (publishStage?.is_completed) {
                        persistentStorage.setItem(ONBOARDING_FINISHED_STORAGE_KEY, '1');
                    }
                },
                providesTags: [OnboardingTag],
            }),
            getCloudPaymentsCallbackUrls: builder.query<
                OpenApiGetCloudPayCallbackUrlResponseSuccess,
                OpenApiGetCloudPayCallbackUrlPathParameters
            >({
                query: () => {
                    return {
                        url: 'onboarding/cloud_pay/callback_urls',
                        method: 'GET',
                    };
                },
            }),
            changeOnboardingStage: builder.mutation<
                OpenApiUpdateStageCompletionResponseSuccess,
                ChangeOnboardingStageRequest
            >({
                query: (stage) => {
                    return {
                        url: `/onboarding/stage`,
                        method: 'PATCH',
                        body: stage,
                    };
                },
                onQueryStarted: async (payload, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getOnboardingStages.updateQueryData(undefined, (data) => {
                        data.stages.forEach((stage) => {
                            if (stage.slug === payload.slug) {
                                stage.is_completed = payload.value;
                            }
                        });
                    });
                },
            }),
            changeOnboardingSubtask: builder.mutation<
                OpenApiUpdateSubtaskCompletionResponseSuccess,
                ChangeOnboardingSubtaskRequest
            >({
                query: (stage) => {
                    return {
                        url: `/onboarding/subtask`,
                        method: 'PATCH',
                        body: stage,
                    };
                },
                onQueryStarted: async (payload, { queryFulfilled }) => {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getOnboardingStages.updateQueryData(undefined, (data) => {
                        data.stages.forEach((stage) => {
                            stage.subtasks.forEach((subtask) => {
                                if (subtask.slug === payload.slug) {
                                    subtask.is_completed = payload.value;
                                }
                            });

                            if (stage.subtasks.length > 0) {
                                stage.is_completed = stage.subtasks.every((subtask) => subtask.is_completed);
                            }
                        });
                    });
                },
            }),
        };
    },
});

export const invalidateOnboardingCache = () => api.util.invalidateTags([OnboardingTag]);

export const changeOnboardingStage = api.endpoints.changeOnboardingStage.mutate;
export const changeOnboardingSubtask = api.endpoints.changeOnboardingSubtask.mutate;
export const getOnboardingStages = api.endpoints.getOnboardingStages.query;

export const useGetOnboardingStages = api.useGetOnboardingStagesQuery;
export const useGetOnboardingCloudPaymentsCallbackUrls = api.useGetCloudPaymentsCallbackUrlsQuery;
